import { useState, useEffect, useRef } from 'react';
import { type NodeProps } from '@xyflow/react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';

export interface NoteNodeData extends Record<string, unknown> {
  text: string;
  onChange?: (text: string) => void;
  onDelete?: () => void;
}

/**
 * Sticky-note annotation node for the workflow canvas.
 * Users drop these with the Comment tool. Click to edit, blur to commit.
 */
export default function NoteNode({ data, selected }: NodeProps) {
  const d = data as NoteNodeData;
  const [editing, setEditing] = useState(!d.text);
  const [draft, setDraft] = useState(d.text || '');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(d.text || '');
  }, [d.text]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (d.text || '')) d.onChange?.(draft);
  };

  return (
    <div
      className={cn(
        'relative flex w-56 min-h-[80px] flex-col rounded-md px-3 py-2 font-display text-sm text-yellow-900 shadow-md transition-shadow',
        'bg-[#fff7c5]',
        selected && 'ring-2 ring-primary/60 ring-offset-2 ring-offset-background',
      )}
      onDoubleClick={() => setEditing(true)}
    >
      {editing ? (
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setDraft(d.text || '');
              setEditing(false);
            }
          }}
          placeholder="Type a note…"
          rows={3}
          className="nodrag nopan w-full flex-1 resize-none border-0 bg-transparent text-sm text-yellow-900 placeholder:text-yellow-900/40 focus:outline-none"
        />
      ) : (
        <p className="whitespace-pre-wrap break-words">{d.text || 'Empty note'}</p>
      )}

      {selected && !editing && (
        <div className="nodrag nopan absolute -top-3 right-2 flex items-center gap-1 rounded-md bg-background/95 px-1 py-0.5 shadow">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[10px] font-medium text-muted-foreground hover:text-foreground"
          >
            Edit
          </button>
          <span className="text-muted-foreground/40">·</span>
          <button
            type="button"
            onClick={() => d.onDelete?.()}
            aria-label="Delete note"
            className="text-muted-foreground hover:text-destructive"
          >
            <TrashIcon className="h-3 w-3" />
          </button>
        </div>
      )}
      {editing && (
        <button
          type="button"
          onClick={() => { setDraft(d.text || ''); setEditing(false); }}
          aria-label="Cancel edit"
          className="nodrag nopan absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-200 text-yellow-900 shadow"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
